// src/components/ServiceNow/RitmOverview.jsx
import React, { useState } from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  IconButton,
  Collapse,
  LinearProgress,
  Tooltip,
  Grid,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Circle as CircleIcon,
} from "@mui/icons-material";
import {
  getDisplayValue,
  getValue,
  getStateLabel,
  getStateColor,
  formatDate,
  getUserDisplayName,
} from "../../utils/serviceNowUtils";

const RitmOverview = ({
  ritm,
  statistics,
  cachedAt,
  onRefresh,
  loading,
  taskStates,
}) => {
  const [expanded, setExpanded] = useState(false);

  if (!ritm) return null;

  const ritmNumber = getValue(ritm.number);
  const state = getValue(ritm.state);
  const stateColor = getStateColor(state);
  const stateLabel = getStateLabel(state, taskStates);
  const shortDescription = getDisplayValue(ritm.short_description);
  const assignedTo = getUserDisplayName(ritm.assigned_to);
  const openedAt = getDisplayValue(ritm.opened_at);
  const updatedBy =
    getUserDisplayName(ritm.sys_updated_by) ||
    getDisplayValue(ritm.sys_updated_by);

  // Calculate risk level based on statistics
  const getRiskLevel = () => {
    if (!statistics) return { label: "Unknown", color: "#9E9E9E" };

    if (statistics.OverdueTasks > 0) {
      return { label: "High Risk", color: "#EF5350" };
    } else if (
      statistics.UnassignedTasks > 0 ||
      statistics.AwaitingInformationTasks > 0
    ) {
      return { label: "Medium Risk", color: "#FFA726" };
    } else if (statistics.InProgressTasks > 0) {
      return { label: "Low Risk", color: "#66BB6A" };
    }
    return { label: "No Risk", color: "#66BB6A" };
  };

  // Get progress bar color based on completion percentage
  const getProgressColor = (percentage) => {
    if (percentage === 100) return "#66BB6A"; // Green - Full completion
    if (percentage >= 70) return "#3299FF"; // Blue - Good progress
    if (percentage >= 40) return "#FFA726"; // Orange - Moderate progress
    return "#EF5350"; // Red - Low progress
  };

  const riskLevel = getRiskLevel();
  const progressColor = getProgressColor(statistics?.CompletionPercentage || 0);

  return (
    <Card sx={{ mb: 3, borderLeft: `4px solid ${stateColor}` }}>
      <CardContent>
        {/* Header Row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: "#1a1a1a",
                fontFamily: "Urbanist, Verdana, Arial, sans-serif",
              }}
            >
              {ritmNumber}
            </Typography>

            <Chip
              label={stateLabel}
              sx={{
                backgroundColor: `${stateColor}20`,
                color: stateColor,
                fontWeight: 600,
                fontSize: "0.875rem",
                fontFamily: "Urbanist, Verdana, Arial, sans-serif",
              }}
            />

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircleIcon sx={{ fontSize: 12, color: riskLevel.color }} />
              <Typography
                variant="body2"
                sx={{
                  color: "#666",
                  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                }}
              >
                {riskLevel.label}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="caption" sx={{ color: "#999" }}>
              Opened: {formatDate(openedAt)}
            </Typography>
            {assignedTo && (
              <Typography
                variant="caption"
                sx={{
                  color: "#999",
                  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                }}
              >
                | Assigned to: {assignedTo}
              </Typography>
            )}

            <Tooltip title="Refresh data">
              <IconButton
                onClick={onRefresh}
                disabled={loading}
                size="small"
                sx={{ ml: 1 }}
              >
                <RefreshIcon
                  sx={{
                    fontSize: 20,
                    animation: loading ? "spin 1s linear infinite" : "none",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }}
                />
              </IconButton>
            </Tooltip>

            <IconButton
              onClick={() => setExpanded(!expanded)}
              size="small"
              sx={{
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s",
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Description */}
        <Typography
          variant="body2"
          sx={{
            color: "#555",
            mb: 2,
            fontFamily: "Urbanist, Verdana, Arial, sans-serif",
          }}
        >
          {shortDescription}
        </Typography>

        {/* Progress Bar */}
        {statistics && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
              <LinearProgress
                variant="determinate"
                value={statistics.CompletionPercentage}
                sx={{
                  flex: 1,
                  height: 12,
                  borderRadius: 1,
                  backgroundColor: "#e5e4e2",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: progressColor,
                    borderRadius: 1,
                  },
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "#555",
                  minWidth: 180,
                  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                }}
              >
                {statistics.CompletedTasks} of {statistics.TotalTasks} tasks
                complete ({statistics.CompletionPercentage}%)
              </Typography>
            </Box>

            {/* Task Summary */}
            <Box
              sx={{
                display: "flex",
                gap: 3,
                fontSize: "0.875rem",
                color: "#666",
              }}
            >
              <Box sx={{ fontFamily: "Urbanist, Verdana, Arial, sans-serif" }}>
                <span style={{ color: "#999" }}>Complete:</span>{" "}
                <span style={{ fontWeight: 600, color: "#66BB6A" }}>
                  {statistics.CompletedTasks}
                </span>
              </Box>
              <Box sx={{ fontFamily: "Urbanist, Verdana, Arial, sans-serif" }}>
                <span style={{ color: "#999" }}>In Progress:</span>{" "}
                <span style={{ fontWeight: 600, color: "#3299FF" }}>
                  {statistics.InProgressTasks}
                </span>
              </Box>
              <Box sx={{ fontFamily: "Urbanist, Verdana, Arial, sans-serif" }}>
                <span style={{ color: "#999" }}>Overdue:</span>{" "}
                <span style={{ fontWeight: 600, color: "#EF5350" }}>
                  {statistics.OverdueTasks}
                </span>
              </Box>
              <Box sx={{ fontFamily: "Urbanist, Verdana, Arial, sans-serif" }}>
                <span style={{ color: "#999" }}>Unassigned:</span>{" "}
                <span style={{ fontWeight: 600, color: "#FFA726" }}>
                  {statistics.UnassignedTasks}
                </span>
              </Box>
            </Box>
          </Box>
        )}

        {/* Expandable Details */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ borderTop: "1px solid #e5e4e2", pt: 2, mt: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
                color: "#555",
                fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                fontWeight: 600,
              }}
            >
              <CircleIcon sx={{ fontSize: 8 }} />
              RITM Activity Log
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ backgroundColor: "#f5f5f5", borderRadius: 1, p: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: "#1a1a1a",
                        fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                      }}
                    >
                      {formatDate(getValue(ritm.sys_updated_on))}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#666",
                        fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                      }}
                    >
                      - {getDisplayValue(ritm.sys_updated_by)}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#555",
                      fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                    }}
                  >
                    RITM {stateLabel} - {statistics?.CompletedTasks || 0} of{" "}
                    {statistics?.TotalTasks || 0} tasks completed
                  </Typography>
                </Box>
              </Grid>

              {cachedAt && (
                <Grid item xs={12}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#999",
                      fontStyle: "italic",
                      fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                    }}
                  >
                    Last fetched: {formatDate(cachedAt)} (cached for 45 seconds)
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default RitmOverview;
